import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getUserInvestmentCount, calculateSubscriptionCost, getAvailableCurrencies } from '../../utils/pricingUtils';
import useLocationDetection from '../../hooks/useLocationDetection';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useSubscriptionModal(isOpen, user, defaultType) {
  const [isLoading, setIsLoading] = useState(false);
  const [investmentCount, setInvestmentCount] = useState(0);
  const [pricingCalculation, setPricingCalculation] = useState(null);
  const [pricingCalculationNGN, setPricingCalculationNGN] = useState(null);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);
  const [useV2Pricing, setUseV2Pricing] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState('monthly');
  const { isNigerian, loading: locationLoading } = useLocationDetection();

  const loadPricingData = useCallback(async () => {
    setIsLoadingPricing(true);
    try {
      const count = await getUserInvestmentCount();
      setInvestmentCount(count);
      const calcUSD = await calculateSubscriptionCost(count, user?.country, 'USD');
      if (calcUSD) {
        setPricingCalculation(calcUSD); setUseV2Pricing(true);
        const currencies = await getAvailableCurrencies(user?.country);
        setAvailableCurrencies(currencies);
        if (isNigerian || user?.country === 'Nigeria') {
          const calcNGN = await calculateSubscriptionCost(count, 'Nigeria', 'NGN');
          if (calcNGN) setPricingCalculationNGN(calcNGN);
        }
      } else { setUseV2Pricing(false); }
    } catch (error) { logger.error('Error loading pricing:', error); setUseV2Pricing(false); }
    finally { setIsLoadingPricing(false); }
  }, [user, isNigerian]);

  useEffect(() => { if (isOpen && user) loadPricingData(); }, [isOpen, user, selectedCurrency, loadPricingData]);
  useEffect(() => { if (isOpen && defaultType) setSubscriptionType(defaultType); }, [isOpen, defaultType]);

  const handleSubscribe = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        body: JSON.stringify({ subscription_tier: 'premium', origin_url: window.location.origin, subscription_type: subscriptionType }),
      });
      if (!res.ok) {
        if (res.status === 401) { toast.error('Session expired. Please login again.'); setTimeout(() => { window.location.href = '/login'; }, 2000); return; }
        const error = await res.json(); throw new Error(error.detail || 'Failed to create checkout session');
      }
      const data = await res.json();
      if (data.url) window.location.href = data.url; else throw new Error('No checkout URL received');
    } catch (error) { logger.error('Checkout error:', error); toast.error(error.message || 'Failed to start checkout process'); }
    finally { setIsLoading(false); }
  };

  return { isLoading, investmentCount, pricingCalculation, pricingCalculationNGN, availableCurrencies, selectedCurrency, setSelectedCurrency, isLoadingPricing, useV2Pricing, subscriptionType, setSubscriptionType, isNigerian, handleSubscribe };
}
